import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollToTop from "@/components/base/ScrollToTop";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  formatViews,
  getArticleById,
  getArticleNeighbors,
  getCategoryName,
  getPopularArticles,
  getRelatedArticles,
} from "@/lib/content";

const ORIGIN = "https://sjlm888.cn";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const [copied, setCopied] = useState(false);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Seo
          title="瓜已经吃完了 - 51吃瓜"
          description="你访问的瓜文不存在或已下线，返回首页看看今天有什么瓜。"
          keywords="51吃瓜,今日新鲜瓜"
          path={`/article/${id}`}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "瓜已经吃完了",
            url: `${ORIGIN}/article/${id}`,
          }}
        />
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-32">
          <div className="text-center max-w-md">
            <i className="ri-plant-line text-5xl text-foreground-300"></i>
            <h1 className="mt-5 font-heading font-black text-2xl text-foreground-950">
              这条瓜已经吃完了
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              它可能被移动或下线了，回首页看看今天有什么新瓜吧。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);
  const { prev, next } = getArticleNeighbors(article.id);
  const popular = getPopularArticles(5);
  const categoryName = getCategoryName(article.category);
  const toc = article.content.filter((block) => block.heading);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.publishedAt,
      dateModified: article.publishedAt,
      articleSection: categoryName,
      keywords: article.tags.join(","),
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "51吃瓜", url: `${ORIGIN}/` },
      mainEntityOfPage: `${ORIGIN}/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: categoryName,
          item: `${ORIGIN}/category/${article.category}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `${ORIGIN}/article/${article.id}`,
        },
      ],
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${ORIGIN}/article/${article.id}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  let headingIndex = 0;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={`${article.title} - 51吃瓜`}
        description={article.excerpt}
        keywords={`51吃瓜,${categoryName},${article.tags.join(",")},51吃瓜今日新鲜瓜`}
        path={`/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full px-4 md:px-8 pt-24 md:pt-32">
          <div className="max-w-content mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-8 pb-14">
          <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                  >
                    {categoryName}
                  </Link>
                  {article.trending && (
                    <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 flex items-center gap-1">
                      <i className="ri-fire-line"></i>
                      热门
                    </span>
                  )}
                </div>
                <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl leading-tight text-foreground-950">
                  {article.title}
                </h1>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line"></i>
                    {article.author}
                  </span>
                  <time className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </time>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readMinutes} 分钟阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {formatViews(article.views)} 阅读
                  </span>
                </div>
              </header>

              <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 51吃瓜`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 text-base leading-loose text-foreground-700">
                {article.content.map((block, index) => {
                  if (block.heading) {
                    headingIndex += 1;
                    const anchor = `section-${headingIndex}`;
                    return (
                      <section key={index} id={anchor} className="scroll-mt-28">
                        <h2 className="mt-8 mb-3 font-heading font-bold text-xl md:text-2xl text-foreground-950">
                          {block.heading}
                        </h2>
                        <p>{block.text}</p>
                      </section>
                    );
                  }
                  return (
                    <p key={index} className="mb-4">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <i className="ri-price-tag-3-line text-foreground-400"></i>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-100">
                <span className="text-sm text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-share-forward-line"></i>
                  觉得这条瓜有意思？
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="sm:ml-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={copied ? "ri-check-line" : "ri-link-m"}></i>
                  {copied ? "链接已复制" : "复制链接分享"}
                </button>
              </div>

              <nav className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {prev ? (
                  <Link
                    to={`/article/${prev.id}`}
                    className="group rounded-lg border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-arrow-left-line"></i>
                      上一条新瓜
                    </span>
                    <span className="mt-2 block text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {prev.title}
                    </span>
                  </Link>
                ) : (
                  <span></span>
                )}
                {next && (
                  <Link
                    to={`/article/${next.id}`}
                    className="group rounded-lg border border-background-200 p-4 hover:border-primary-300 transition-colors sm:text-right cursor-pointer"
                  >
                    <span className="text-xs text-foreground-400 flex items-center gap-1 sm:justify-end">
                      下一条新瓜
                      <i className="ri-arrow-right-line"></i>
                    </span>
                    <span className="mt-2 block text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {next.title}
                    </span>
                  </Link>
                )}
              </nav>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                {toc.length > 0 && (
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                      <i className="ri-list-unordered text-primary-500"></i>
                      本文目录
                    </h2>
                    <ul className="mt-3 space-y-2">
                      {toc.map((block, index) => (
                        <li key={block.heading}>
                          <a
                            href={`#section-${index + 1}`}
                            className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex gap-2"
                          >
                            <span className="text-primary-400">{index + 1}.</span>
                            {block.heading}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                    <i className="ri-fire-fill text-primary-500"></i>
                    大家都在看
                  </h2>
                  <ul className="mt-3 divide-y divide-background-200">
                    {popular.map((item) => (
                      <li key={item.id}>
                        <ArticleCard article={item} variant="compact" />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full px-4 md:px-8 py-12 bg-background-100">
            <div className="max-w-content mx-auto">
              <h2 className="font-heading font-black text-2xl text-foreground-950">
                相关推荐
              </h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}